import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { IconButton } from "@mui/material";
import {
  CheckRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  KeyboardArrowDownRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";

/**
 * Custom inline dropdown. Renders the options list as an absolutely-positioned
 * child of the trigger, so it stays inside the DatePicker's Paper in the DOM
 * tree. This avoids two problems that MUI Select/Menu have inside a picker:
 *   1. With Portal (default): the menu goes to document.body → DatePicker's
 *      ClickAwayListener treats clicks on options as "outside" and closes.
 *   2. With disablePortal:true: the menu renders inline but has no positioning
 *      logic of its own, causing layout jitter.
 *
 * This component owns its open state, handles its own outside-click via a
 * mousedown listener scoped to its own ref, and scrolls the selected option
 * into view when opening.
 */
const InlineDropdown = ({ value, options, onChange, ariaLabel, minWidth = 110 }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  // Close on outside click (mousedown, not click — fires earlier so the list
  // closes before an adjacent field gets focus).
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Scroll selected option into view when the list opens.
  useLayoutEffect(() => {
    if (open && selectedRef.current && listRef.current) {
      selectedRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={
          "inline-flex items-center justify-between gap-1 bg-white " +
          "border border-gray-200 rounded-[8px] px-2 h-[30px] " +
          "text-[13px] font-medium text-gray-700 " +
          "hover:border-gray-300 hover:bg-gray-50 " +
          "focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 " +
          "transition-colors duration-150 cursor-pointer"
        }
        style={{ minWidth }}
      >
        <span className="truncate">{current?.label ?? ""}</span>
        <KeyboardArrowDownRounded
          sx={{
            fontSize: 16,
            color: "#94a3b8",
            transition: "transform 150ms",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      {open && (
        <div
          ref={listRef}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute z-50 mt-1 bg-white rounded-[10px] border border-gray-200 shadow-lg overflow-auto"
          style={{
            top: "100%",
            left: 0,
            minWidth: "100%",
            maxHeight: 240,
            animation: "inputDateDropIn 140ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            const isDisabled = !!opt.disabled;
            return (
              <div
                key={opt.value}
                ref={isSelected ? selectedRef : null}
                role="option"
                aria-selected={isSelected}
                aria-disabled={isDisabled}
                onMouseDown={(e) => {
                  // Prevent the button blur that would trigger outside-click close
                  // before the onClick runs.
                  e.preventDefault();
                }}
                onClick={() => {
                  if (isDisabled) return;
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={
                  "flex items-center justify-between gap-2 px-2.5 py-[6px] text-[13px] " +
                  (isDisabled
                    ? "text-gray-300 cursor-not-allowed "
                    : "text-gray-700 hover:bg-gray-50 cursor-pointer ") +
                  (isSelected ? "!bg-blue-50 !text-blue-700 font-semibold" : "font-medium")
                }
              >
                <span className="truncate">{opt.label}</span>
                {isSelected && <CheckRounded sx={{ fontSize: 15, color: "#2563eb" }} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const InlineCalendarHeader = ({ currentMonth, onMonthChange, minDate, maxDate }) => {
  const cur = dayjs(currentMonth);
  const monthIdx = cur.month();
  const year = cur.year();

  const todayYear = dayjs().year();
  const fromYear = minDate ? dayjs(minDate).year() : todayYear - 20;
  const toYear = maxDate ? dayjs(maxDate).year() : todayYear + 20;

  // Year options, most recent first (common picks are near the top).
  const yearOptions = [];
  for (let y = toYear; y >= fromYear; y--) yearOptions.push({ value: y, label: String(y) });

  const min = minDate ? dayjs(minDate) : null;
  const max = maxDate ? dayjs(maxDate) : null;

  const canPrev = !min || cur.startOf("month").isAfter(min.startOf("month"));
  const canNext = !max || cur.startOf("month").isBefore(max.startOf("month"));

  // Per-month availability in the currently selected year.
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const candidate = cur.year(year).month(i);
    const beforeMin = min && candidate.endOf("month").isBefore(min, "day");
    const afterMax = max && candidate.startOf("month").isAfter(max, "day");
    return {
      value: i,
      label: dayjs().month(i).format("MMMM"),
      disabled: beforeMin || afterMax,
    };
  });

  const clamp = (target) => {
    let t = target;
    if (min && t.isBefore(min, "day")) t = min;
    if (max && t.isAfter(max, "day")) t = max;
    return t;
  };

  const jumpTo = (next) => {
    const target = clamp(next);
    const direction = target.isBefore(cur) ? "right" : "left";
    onMonthChange(target, direction);
  };

  return (
    <div className="flex items-center justify-between gap-2 px-3 pt-2 pb-1">
      <IconButton
        size="small"
        aria-label="Previous month"
        disabled={!canPrev}
        onClick={() => jumpTo(cur.subtract(1, "month"))}
        sx={{ width: 30, height: 30 }}
      >
        <ChevronLeftRounded fontSize="small" />
      </IconButton>

      <div className="flex items-center gap-1">
        <InlineDropdown
          value={monthIdx}
          options={monthOptions}
          onChange={(v) => jumpTo(cur.month(v))}
          ariaLabel="Select month"
          minWidth={118}
        />
        <InlineDropdown
          value={year}
          options={yearOptions}
          onChange={(v) => jumpTo(cur.year(v))}
          ariaLabel="Select year"
          minWidth={88}
        />
      </div>

      <IconButton
        size="small"
        aria-label="Next month"
        disabled={!canNext}
        onClick={() => jumpTo(cur.add(1, "month"))}
        sx={{ width: 30, height: 30 }}
      >
        <ChevronRightRounded fontSize="small" />
      </IconButton>

      {/* Keyframes injected once per render but deduped by the browser. */}
      <style>{`
        @keyframes inputDateDropIn {
          from { opacity: 0; transform: translateY(-4px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
    </div>
  );
};

const InputDate = (props) => {
  const {
    label,
    variant = "outlined",
    size,
    name,
    value,
    onChange,
    error,
    minDate,
    maxDate,
    openTo = "day",
    fullWidth = true,
    required = false,
    disabled = false,
    views = ["day"],
    format = "DD MMM, YYYY",
    ...other
  } = props;

  // Guard against the picker emitting an Invalid Date while the user is mid-typing
  // (e.g. right after a backspace clears a section). Forwarding such a value lets
  // consumers store an Invalid Date and later crash on `.toISOString()`/`.format()`.
  // We forward only `null` (cleared) and valid values; invalid intermediates are
  // swallowed so the parent keeps its last good value.
  const handleChange = (newVal, ctx) => {
    if (!onChange) return;
    if (newVal == null) return onChange(null, ctx);
    if (dayjs(newVal).isValid()) return onChange(newVal, ctx);
  };

  // Placeholder mirrors the actual format so the hint matches what the user types.
  const placeholder = /[A-Za-z]{3,}/.test(format) ? "DD Mon YYYY" : format;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        name={name}
        value={(value && dayjs(value)) || null}
        onChange={handleChange}
        views={views}
        format={format}
        openTo={openTo}
        // Pulled out of `other` on purpose: left in the rest props it only reaches the
        // TextField, which greys the box but still opens the calendar from the icon.
        disabled={disabled}
        minDate={(minDate && dayjs(minDate)) || null}
        maxDate={(maxDate && dayjs(maxDate)) || null}
        slots={{ calendarHeader: InlineCalendarHeader }}
        slotProps={{
          textField: {
            variant: variant,
            size: size || "small",
            fullWidth: fullWidth,
            ...other,
            ...(error && { error: true, helperText: error }),
            required: required,
            disabled: disabled,
            placeholder: placeholder,
          },

          desktopPaper: {
            style: {
              backgroundColor: "var(--white-color)",
              boxShadow: "none",
              border: "1px solid var(--primaryMD-color)",
            },
          },
          mobilePaper: {
            style: {
              backgroundColor: "var(--white-color)",
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default InputDate;
